import {
  AlertCircle,
  BarChart3,
  Clock,
  Eye,
  Hash,
  LineChart,
  TrendingDown,
  TrendingUp,
  Zap,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { SectionHeader } from "../../components";
import {
  HashtagTrend,
  TimeSlotAnalysis,
  TrendData,
} from "../../types/analytics";
import { cn } from "../../utils/cn";
import Card from "../ui/Card";

import TrendAnalysisSkeleton from "./skeletons/TrendAnalysisSkeleton";

const TrendAnalysis: React.FC = () => {
  const [trends, setTrends] = useState<TrendData[]>([]);
  const [hashtagTrends, setHashtagTrends] = useState<HashtagTrend[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlotAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<"7d" | "30d" | "90d">(
    "30d",
  );

  useEffect(() => {
    // Simular dados de tendências (em uma implementação real, viria da API)
    const mockTrends: TrendData[] = [
      {
        metric: "Taxa de Engajamento",
        current_value: 4.2,
        predicted_value: 5.1,
        trend: "up",
        confidence: 0.87,
        recommendation:
          "Manter estratégia visual intensiva e foco em vídeos curtos.",
      },
      {
        metric: "Alcance Médio",
        current_value: 1250,
        predicted_value: 1450,
        trend: "up",
        confidence: 0.73,
        recommendation: "Otimizar distribuição orgânica entre 14h e 16h.",
      },
      {
        metric: "Interações",
        current_value: 8.5,
        predicted_value: 7.2,
        trend: "down",
        confidence: 0.65,
        recommendation: "Implementar CTAs diretas para incentivar diálogos.",
      },
      {
        metric: "Virabilidade",
        current_value: 3.2,
        predicted_value: 3.8,
        trend: "up",
        confidence: 0.79,
        recommendation:
          "Priorizar conteúdos de valor educativo e compartilhável.",
      },
    ];

    const mockHashtags: HashtagTrend[] = [
      {
        hashtag: "#MarketingDigital",
        usage_frequency: 85,
        engagement_rate: 6.8,
        trend: "rising",
        suggested_posts: 12,
      },
      {
        hashtag: "#IA",
        usage_frequency: 92,
        engagement_rate: 8.4,
        trend: "rising",
        suggested_posts: 15,
      },
      {
        hashtag: "#Tecnologia",
        usage_frequency: 78,
        engagement_rate: 5.2,
        trend: "stable",
        suggested_posts: 8,
      },
      {
        hashtag: "#SaaS",
        usage_frequency: 45,
        engagement_rate: 4.1,
        trend: "falling",
        suggested_posts: 3,
      },
      {
        hashtag: "#Inovação",
        usage_frequency: 67,
        engagement_rate: 7.1,
        trend: "rising",
        suggested_posts: 10,
      },
    ];

    const mockTimeSlots: TimeSlotAnalysis[] = [
      {
        hour: 9,
        day_name: "Segunda",
        engagement_score: 7.8,
        optimal_content_type: "Motivacional",
        audience_size: 1200,
      },
      {
        hour: 14,
        day_name: "Terça",
        engagement_score: 9.2,
        optimal_content_type: "Instrucional",
        audience_size: 1850,
      },
      {
        hour: 18,
        day_name: "Quarta",
        engagement_score: 8.5,
        optimal_content_type: "Dashboard",
        audience_size: 1650,
      },
      {
        hour: 12,
        day_name: "Quinta",
        engagement_score: 8.9,
        optimal_content_type: "Newsletter",
        audience_size: 1750,
      },
      {
        hour: 16,
        day_name: "Sexta",
        engagement_score: 8.1,
        optimal_content_type: "Recap semanal",
        audience_size: 1400,
      },
    ];

    setTrends(mockTrends);
    setHashtagTrends(mockHashtags);
    setTimeSlots(mockTimeSlots);
    setLoading(false);
  }, [selectedPeriod]);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case "up":
        return <TrendingUp size={16} className="text-emerald-500" />;
      case "down":
        return <TrendingDown size={16} className="text-rose-500" />;
      default:
        return <BarChart3 size={16} className="text-gray-400" />;
    }
  };

  if (loading) {
    return <TrendAnalysisSkeleton />;
  }

  return (
    <div className="space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Esta aba ainda não está conectada a uma fonte real de tendências —
          os dados abaixo são de demonstração até a integração ser feita.
          Ver achado na auditoria: "TrendAnalysis 100% mock". */}
      <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50/60 text-amber-700">
        <AlertCircle size={15} className="shrink-0 text-amber-500" />
        <p className="text-xs font-semibold">
          Exibindo <strong>dados de demonstração</strong> — a integração com o
          histórico real de tendências ainda não está disponível nesta aba.
        </p>
      </div>

      {/* Dynamic Header — mesmo tratamento visual do header de "Perfil
          Estratégico do ICP" (EmpathyMapDisplay.tsx): SectionHeader real
          dentro do seu próprio card. */}
      <div className="bg-white border border-purple-200 rounded-xl p-3 shadow-xs">
        <SectionHeader
          icon={<LineChart className="h-5 w-5" />}
          title="Análise de Tendência"
          subtitle="Dados de demonstração — integração com histórico real ainda não disponível"
          hideBorder
          action={
            <div className="flex p-1 bg-gray-50/80 rounded-lg border border-purple-200 self-center">
              {(["7d", "30d", "90d"] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={cn(
                    "px-3 py-1.5 rounded-md text-[10px] font-black uppercase tracking-widest transition-all duration-300",
                    selectedPeriod === period
                      ? "bg-purple-600 text-white shadow-xs"
                      : "text-gray-400 hover:text-purple-600 hover:bg-white",
                  )}
                >
                  {period === "7d"
                    ? "7 Dias"
                    : period === "30d"
                      ? "30 Dias"
                      : "90 Dias"}
                </button>
              ))}
            </div>
          }
        />
      </div>

      {/* Metric Trajectories */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {trends.map((trend, index) => (
          <Card
            key={index}
            className="p-3 border-purple-200 hover:shadow-md transition-all duration-500 group relative overflow-hidden bg-white/60 backdrop-blur-md"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-50 rounded-full -mr-16 -mt-16 opacity-50 transition-transform group-hover:scale-150 duration-700"></div>

            <div className="relative z-10">
              <SectionHeader
                icon={<LineChart className="h-5 w-5" />}
                title={trend.metric}
                subtitle="Projeção IA para os próximos 30 dias"
                action={
                  <div className="text-right self-center">
                    <p className="text-[9px] font-black uppercase tracking-widest text-gray-400">
                      IA Confidence
                    </p>
                    <p className="text-xs font-black text-purple-600">
                      {(trend.confidence * 100).toFixed(0)}%
                    </p>
                  </div>
                }
              />
            </div>

            <div className="flex justify-between items-end mb-3 relative z-10">
              <div className="space-y-1">
                <p className="text-[9px] font-black uppercase tracking-widest text-gray-400">
                  Benchmark Atual
                </p>
                <p className="text-4xl font-black tracking-tighter text-gray-900">
                  {trend.current_value.toFixed(1)}
                  {trend.metric.includes("Taxa") ||
                  trend.metric.includes("Interações")
                    ? "%"
                    : ""}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      "p-1.5 rounded-lg",
                      trend.trend === "up" ? "bg-emerald-50" : "bg-rose-50",
                    )}
                  >
                    {getTrendIcon(trend.trend)}
                  </div>
                  <p
                    className={cn(
                      "text-2xl font-black tracking-tighter",
                      trend.trend === "up"
                        ? "text-emerald-500"
                        : "text-rose-500",
                    )}
                  >
                    {trend.predicted_value.toFixed(1)}
                    {trend.metric.includes("Taxa") ||
                    trend.metric.includes("Interações")
                      ? "%"
                      : ""}
                  </p>
                </div>
                <p className="text-[9px] font-black uppercase tracking-widest text-gray-400">
                  Previsão 30d
                </p>
              </div>
            </div>

            <div className="p-3 bg-purple-50/50 rounded-xl border border-purple-200 flex items-start gap-4 transition-all group-hover:bg-purple-50 relative z-10">
              <div className="p-2 bg-white rounded-xl shadow-xs">
                <Zap size={16} className="text-purple-600" />
              </div>
              <p className="text-xs font-semibold text-purple-900 leading-relaxed italic">
                "{trend.recommendation}"
              </p>
            </div>
          </Card>
        ))}
      </div>

      {/* Hashtag Velocity Section */}
      <div className="space-y-2">
        <div className="flex items-center gap-4 px-2">
          <div className="p-2 bg-purple-50 rounded-xl">
            <Hash size={20} className="text-purple-600" />
          </div>
          <div>
            <h3 className="text-sm font-black uppercase tracking-widest text-gray-900">
              Velocidade de Hashtags
            </h3>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Fluxo Semântico & Tração
            </p>
          </div>
        </div>

        {/* p-0! explícito: Card.tsx tem padding default (p-4 sm:p-4.5...)
            que um simples `pb-4` na className não sobrescreve de forma
            confiável (cn() é clsx puro, sem tailwind-merge — a ordem final
            das classes no CSS gerado decide, não a ordem no JSX). A tabela
            já tem seu próprio px-3 nas células, então o card não deve ter
            padding horizontal/superior — só o pb-4 abaixo, aplicado no
            wrapper interno em vez de no Card. */}
        <Card className="overflow-hidden border-purple-200 bg-white/60 backdrop-blur-md p-0!">
          <div className="overflow-x-auto pb-4">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/40">
                  {[
                    "Etiqueta",
                    "Intensidade de Uso",
                    "Conversão IA",
                    "Impulso",
                    "Volumen",
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left py-3 px-3 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 border-b border-purple-200"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-purple-200/30">
                {hashtagTrends.map((hashtag, index) => {
                  const trendStyleMap = {
                    rising: "bg-emerald-50 text-emerald-600 border-emerald-100",
                    falling: "bg-rose-50 text-rose-600 border-rose-100",
                    stable: "bg-indigo-50 text-indigo-600 border-purple-200",
                  };
                  return (
                    <tr
                      key={index}
                      className="hover:bg-purple-50/20 transition-all duration-300 group"
                    >
                      <td className="py-3 px-3">
                        <span className="text-sm font-black text-gray-900 group-hover:text-purple-600 transition-colors">
                          {hashtag.hashtag}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-4">
                          <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden border border-purple-200">
                            <div
                              className="h-full bg-purple-500 rounded-full"
                              style={{ width: `${hashtag.usage_frequency}%` }}
                            />
                          </div>
                          <span className="text-[10px] font-black text-gray-400">
                            {hashtag.usage_frequency}%
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <span className="text-sm font-black text-emerald-600">
                          {hashtag.engagement_rate}%
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <span
                          className={cn(
                            "px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border",
                            trendStyleMap[
                              hashtag.trend as keyof typeof trendStyleMap
                            ] || "bg-gray-50 text-gray-500 border-purple-200",
                          )}
                        >
                          {hashtag.trend === "rising"
                            ? "Expansivo"
                            : hashtag.trend === "falling"
                              ? "Retração"
                              : "Platô"}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <Eye size={12} className="text-gray-300" />
                          <span className="font-black text-[11px] text-gray-400 uppercase tracking-widest">
                            {hashtag.suggested_posts} UNITS
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Golden Slots Section */}
      <div className="space-y-2">
        <div className="flex items-center gap-4 px-2">
          <div className="p-2 bg-amber-50 rounded-xl">
            <Clock size={20} className="text-amber-500" />
          </div>
          <div>
            <h3 className="text-sm font-black uppercase tracking-widest text-gray-900">
              Janelas de Ouro Técnicas
            </h3>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Picos de Atividade da Audiência
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {timeSlots.map((slot, index) => (
            <Card
              key={index}
              className="p-3 border-purple-200 bg-white hover:shadow-md hover:scale-[1.02] transition-all duration-500 group"
            >
              <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-2">
                {slot.day_name}
              </p>
              <div className="text-3xl font-black tracking-tighter text-gray-900 mb-3 group-hover:text-purple-600 transition-colors">
                {slot.hour}:00
              </div>

              <div className="space-y-2">
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-[9px] font-black uppercase tracking-widest text-gray-400 px-1">
                    <span>IA Score</span>
                    <span className="text-purple-600">
                      {slot.engagement_score}/10
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-50 rounded-full w-full overflow-hidden border border-purple-200">
                    <div
                      className="h-full bg-purple-500 rounded-full transition-all duration-1000"
                      style={{ width: `${slot.engagement_score * 10}%` }}
                    />
                  </div>
                </div>
                <div className="px-4 py-2.5 bg-gray-50 text-gray-500 text-[9px] font-black uppercase tracking-widest rounded-xl border border-purple-200 text-center group-hover:bg-purple-50 group-hover:text-purple-600 transition-colors">
                  {slot.optimal_content_type}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Strategic Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        <Card className="p-3 bg-emerald-50/30 border-emerald-100 shadow-xs backdrop-blur-md">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-white rounded-lg shadow-xs">
              <TrendingUp className="text-emerald-500" size={24} />
            </div>
            <div>
              <h3 className="text-sm font-black uppercase tracking-widest text-emerald-900">
                Vantagem Competitiva
              </h3>
              <p className="text-[9px] font-black uppercase tracking-widest text-amber-600 mt-0.5">
                Demonstração
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {[
              {
                text: "Aceleração máxima identificada em #IA.",
                impact: "+22.4%",
              },
              {
                text: "Otimização prioritária: Terças às 14h.",
                impact: "+15.2%",
              },
              {
                text: "Densidade informativa ideal: 150-180 chars.",
                impact: "+8.9%",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 bg-white border border-emerald-50 rounded-xl shadow-xs hover:shadow-md transition-all group"
              >
                <span className="text-xs font-bold text-gray-700">
                  {item.text}
                </span>
                <span className="text-[10px] font-black text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
                  {item.impact}
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-3 bg-rose-50/20 border-rose-100 shadow-xs backdrop-blur-md">
          <SectionHeader
            icon={<TrendingDown className="h-5 w-5" />}
            title="Pontos de Saturação"
            subtitle="Demonstração — riscos identificados no ciclo atual"
            iconBgClass="bg-rose-500"
          />
          <div className="space-y-2">
            {[
              {
                text: "Retração orgânica em hashtags saturadas.",
                risk: "Alto",
              },
              { text: "Engajamento volátil pós-20h.", risk: "Médio" },
              { text: "Inconsistência de resposta em threads.", risk: "Baixo" },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 bg-white border border-rose-50 rounded-xl shadow-xs hover:shadow-md transition-all group"
              >
                <span className="text-xs font-bold text-gray-700">
                  {item.text}
                </span>
                <span
                  className={cn(
                    "text-[9px] font-black px-3 py-1 rounded-lg uppercase tracking-widest",
                    item.risk === "Alto"
                      ? "bg-rose-100 text-rose-600"
                      : item.risk === "Médio"
                        ? "bg-amber-100 text-amber-600"
                        : "bg-gray-100 text-gray-500",
                  )}
                >
                  {item.risk} Risco
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TrendAnalysis;
